import { useEffect, useRef, useState } from 'react';

import { getInitials } from '@/lib/initials';

const SIZES = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-9 w-9 text-[12px]',
  md: 'h-10 w-10 text-[13px]',
  lg: 'h-12 w-12 text-[15px]',
} as const;

/** Color de fondo por defecto (azul Icaria) cuando el usuario no eligió uno. */
const DEFAULT_COLOR = '#1B23B8';
/** Verde "dentro de la oficina" (estado operativo en el panel de equipo). */
const ACTIVE_COLOR = '#1F6B3E';
/** Gris "inactivo" (usuario dado de baja, en los listados de admin). */
const MUTED_COLOR = '#6B6A5E';

/** Tamaño (px) de la imagen ampliada del preview al hacer hover. */
const PREVIEW_IMG = 168;
/** Ancho/alto aproximados del recuadro de preview, para clamp al viewport. */
const PREVIEW_W = PREVIEW_IMG + 8;
const PREVIEW_H = PREVIEW_IMG + 36;

export interface AvatarProps {
  fullName: string;
  /** Color de fondo elegido (hex de paleta); null/undefined = por defecto. */
  color?: string | null;
  /** URL de la foto; null/undefined = mostrar círculo con iniciales. */
  avatarUrl?: string | null;
  /** Estado "dentro" (verde). Tiene prioridad sobre `color` cuando no hay foto. */
  active?: boolean;
  /** Estado "inactivo" (gris). Tiene prioridad sobre `color` cuando no hay foto. */
  muted?: boolean;
  size?: keyof typeof SIZES;
  className?: string;
  /**
   * Si hay foto, al pasar el ratón muestra una versión ampliada flotante con
   * el nombre. Pensado para vistas con avatares pequeños (calendario de
   * equipo) donde cuesta reconocer la cara.
   */
  previewOnHover?: boolean;
}

/**
 * Avatar de usuario unificado. Si hay foto (`avatarUrl`), la foto manda en
 * todas las vistas. Sin foto, se pinta un círculo con las iniciales sobre:
 *  - verde si `active`, gris si `muted` (estados operativos), o
 *  - el `color` personalizado, o el azul por defecto.
 *
 * Si la imagen no carga (404 en carrera, foto corrupta) cae a las iniciales.
 */
export function Avatar({
  fullName,
  color,
  avatarUrl,
  active,
  muted,
  size = 'sm',
  className = '',
  previewOnHover = false,
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [previewPos, setPreviewPos] = useState<{ left: number; top: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  // Resetear el fallback cuando cambia la URL (p. ej. tras subir una foto nueva).
  useEffect(() => setImgFailed(false), [avatarUrl]);

  const showImg = Boolean(avatarUrl) && !imgFailed;
  const bg = active ? ACTIVE_COLOR : muted ? MUTED_COLOR : (color ?? DEFAULT_COLOR);
  const canPreview = previewOnHover && showImg;

  function openPreview() {
    if (!canPreview || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    // Por defecto a la derecha del avatar; si no cabe, a la izquierda.
    let left = r.right + 10;
    if (left + PREVIEW_W > window.innerWidth) left = r.left - PREVIEW_W - 10;
    const top = Math.max(
      8,
      Math.min(r.top + r.height / 2 - PREVIEW_H / 2, window.innerHeight - PREVIEW_H - 8),
    );
    setPreviewPos({ left, top });
  }

  return (
    <>
      <span
        ref={ref}
        className={`inline-flex ${SIZES[size]} flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-mono font-semibold tracking-[0.02em] text-white ${className}`}
        style={{ backgroundColor: showImg ? undefined : bg }}
        title={fullName}
        aria-label={fullName}
        onMouseEnter={openPreview}
        onMouseLeave={() => setPreviewPos(null)}
      >
        {showImg ? (
          <img
            src={avatarUrl ?? undefined}
            alt={fullName}
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          getInitials(fullName)
        )}
      </span>

      {canPreview && previewPos ? (
        <div
          className="pointer-events-none fixed z-50 flex flex-col items-center gap-1.5 rounded-md border border-border bg-card p-1.5 shadow-lg"
          style={{ left: previewPos.left, top: previewPos.top }}
        >
          <img
            src={avatarUrl ?? undefined}
            alt={fullName}
            className="rounded object-cover"
            style={{ width: PREVIEW_IMG, height: PREVIEW_IMG }}
          />
          <span className="max-w-[168px] truncate px-1 text-[12px] font-semibold text-foreground">
            {fullName}
          </span>
        </div>
      ) : null}
    </>
  );
}
